import React from "react";

import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font
} from "@react-pdf/renderer";
import { IVarietyDataSheet } from "../../../components/UI/VarietyDataSheet/VarietyDataSheet";
import { useTranslation } from "react-i18next";
import {
  TRADE_NAME,
  TRADE_MARK,
  DENOMINATION,
  convertDate,
  GLOBAL_DATE_FORMAT,
  SYNONYM,
} from "../../../shared/utility";
import { IInvalidVarieties } from "../../../components/TestDataSheet/TestDataSheet";

Font.register({ family: 'Open Sans', fonts: [
  { src: '/OpenSans-Regular.ttf' },
  { src: '/OpenSans-Bold.ttf' , fontWeight: 'bold'}
]});

// Create styles
const styles = StyleSheet.create({
  page: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    margin: 10,
    padding: 10,
    fontFamily: "Open Sans"
  },
  row: {
    flexDirection: "row",
    justifyContent: "center",
    flexGrow: 0,
    flexShrink: 0,
  },
  headerLeft: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 200,
  },
  headerRight: {
    flexShrink: 0,
    flexGrow: 0,
    flexBasis: 300,
    alignSelf: "center",
  },
  headerText: {
    paddingLeft: 30,
    fontSize: 20,
    fontFamily: "Open Sans",
    fontWeight: "ultrabold",
    color: "#2F932F"
  },
  col: {
    flexGrow: 0,
    flexShrink: 0,
  },
  textKey: {
    paddingTop: 4,
    paddingBottom: 4,
    fontFamily: "Open Sans",
    fontWeight: "ultrabold",
    fontSize: 9,
    color: "#867B5E",
  },
  textValue: {
    border: 1,
    fontSize: 9,
    padding: 4,
  },
  regionTitle: {
    fontFamily: "Open Sans",
    fontWeight: "ultrabold",
    fontSize: 14,
    color: "#2F932F",
  },
  fullRow: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 500,
  },
  pageNumber: {
    position: "absolute",
    fontSize: 8,
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: "center",
  },
  table: {
    // @ts-ignore
    display: "table",
    width: "auto",
    marginRight: 38,
    marginLeft: 38,
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderColor: "#AFA794",
  },
  tableRow: {
    margin: "auto",
    flexDirection: "row",
  },
  tableCol: {
    width: "90%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "#AFA794",
  },
  tableColNum: {
    width: "10%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "#AFA794",
  },
  tableHeaderCol: {
    fontFamily: "Open Sans",
    fontWeight: "ultrabold",
    backgroundColor: "#EFE7DE",
    color: "#867A5E"
  },
  tableCell: {
    fontSize: 8,
    paddingHorizontal: 4,
    paddingVertical: 2,
    justifyContent: "center"
  },
  image: { width: 200, backgroundColor: "#ffffff" },
});

// Create Document Component
export const VarietyDataSheetPDF = (props: {
  data: IVarietyDataSheet[];
  invalidDenoms?: IInvalidVarieties[];
}) => {
  const { t } = useTranslation();

  const denominationList: any[] = [];
  const tradenameList: string[][] = [];
  const trademarkList: string[][] = [];
  const synonymsList: string[][] = [];

  props.data.forEach((datasheet: IVarietyDataSheet, i: number) => {
    denominationList[i] = JSON.parse(datasheet.denominationsList);

    trademarkList[i] = [];
    tradenameList[i] = [];
    synonymsList[i] = [];

    denominationList[i]?.denos.forEach((item: any) => {
      if (item?.nature === TRADE_MARK && item.name) {
        trademarkList[i].push(item.name);
      } else if (item?.nature === TRADE_NAME && item.name) {
        tradenameList[i].push(item.name);
      } else if (item?.nature === SYNONYM && item.name) {
        synonymsList[i].push(item.name);
      }
    });
  });

  const invalidDenominations = (
    <>
      {props.invalidDenoms && props.invalidDenoms.length > 0 ? (
        <Page size="A4" style={styles.page}>
          <View style={[styles.row, { paddingTop: 10 }]}>
            <View style={styles.headerLeft}>
              <Image style={styles.image} src="/logo-en.png" />
            </View>
            <View style={styles.headerRight}>
              <Text style={styles.headerText}>
                {t("invalid_denominations_pdf")}
              </Text>
            </View>
          </View>

          <View style={[styles.table, { marginTop: 5 }]}>
            {/* TableHeader */}
            <View style={styles.tableRow}>
              <View style={[styles.tableHeaderCol, styles.tableColNum]}>
                <Text style={styles.tableCell}>No.</Text>
              </View>
              <View style={[styles.tableHeaderCol, styles.tableCol]}>
                <Text style={styles.tableCell}>{t("denomination")}</Text>
              </View>
            </View>

            {props.invalidDenoms.map((data: any, i: number) => (
              <View style={styles.tableRow} key={i}>
                <View style={styles.tableColNum}>
                  <Text style={styles.tableCell}>{i + 1}</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>{data}</Text>
                </View>
              </View>
            ))}
          </View>

          <Text
            style={{
              position: "absolute",
              bottom: 33,
              left: 40,
              right: 320,
              borderBottom: 1,
            }}
            fixed
          />

          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) =>
              `${pageNumber} / ${totalPages}`
            }
            fixed
          />

          <Text
            style={{
              position: "absolute",
              bottom: 33,
              left: 320,
              right: 40,
              borderBottom: 1,
            }}
            fixed
          />
        </Page>
      ) : (
        <></>
      )}
    </>
  );

  return (
    <Document>
      <>
        {props.data.map((datasheet: IVarietyDataSheet, i: number) => (
          <Page key={`datasheet-${i}`} size="A4" style={styles.page}>
            <View style={[styles.row, { paddingTop: 10 }]}>
              <View style={styles.headerLeft}>
                <Image style={styles.image} src="/logo-en.png" />
              </View>
              <View style={styles.headerRight}>
                <Text style={styles.headerText}>{t("variety_datasheet")}</Text>
              </View>
            </View>

            <View style={[styles.row, { paddingTop: 5 }]}>
              <View style={[styles.col, { flexBasis: 100 }]}>
                <Text style={styles.textKey}>{t("country")}</Text>
              </View>
              <View style={[styles.col, { flexBasis: 400 }]}>
                <Text style={styles.textValue}>
                  {datasheet.countryid
                    ? `${datasheet.countryid} - ${datasheet.country}`
                    : ""}
                </Text>
              </View>
            </View>

            <View style={[styles.row, { paddingTop: 5 }]}>
              <View style={[styles.col, { flexBasis: 100 }]}>
                <Text style={styles.textKey}>{t("register_type")}</Text>
              </View>
              <View style={[styles.col, { flexBasis: 400 }]}>
                <Text style={styles.textValue}>
                  {printHtmlField(datasheet.pub_type_name)}
                </Text>
              </View>
            </View>

            <View style={[styles.row, { paddingTop: 5 }]}>
              <View style={[styles.col, { flexBasis: 100 }]}>
                <Text style={styles.textKey}>{t("register_name")}</Text>
              </View>
              <View style={[styles.col, { flexBasis: 400 }]}>
                <Text style={styles.textValue}>
                  {printHtmlField(datasheet.pub_name)}
                </Text>
              </View>
            </View>

            <View style={[styles.row, { paddingTop: 5 }]}>
              <View style={[styles.col, { flexBasis: 100 }]}>
                <Text style={styles.textKey}>{t("species")}</Text>
              </View>
              <View style={[styles.col, { flexBasis: 400 }]}>
                <Text style={styles.textValue}>
                  {printHtmlField(datasheet.species_princ_bot_name)}
                </Text>
              </View>
            </View>

            <View style={[styles.row, { paddingTop: 5 }]}>
              <View style={[styles.col, { flexBasis: 100 }]}>
                <Text style={styles.textKey}>{t("variety_status")}</Text>
              </View>
              <View style={[styles.col, { flexBasis: 400 }]}>
                <Text style={styles.textValue}>
                  {printHtmlField(datasheet.reg_status)}
                </Text>
              </View>
            </View>

            <View style={[styles.row, { paddingTop: 20 }]}>
              <View style={[styles.col, { flexBasis: 250 }]}>
                <Text style={styles.regionTitle}>
                  {t("application_filing")}
                </Text>
              </View>
              <View style={[styles.col, { flexBasis: 250 }]}>
                <Text style={[styles.regionTitle, { paddingLeft: 10 }]}>
                  {t("grant_registration")}
                </Text>
              </View>
            </View>

            <View style={[styles.row, { paddingTop: 10 }]}>
              <View style={[styles.col, { flexBasis: 100 }]}>
                <Text style={styles.textKey}>{t("number")}</Text>
              </View>
              <View style={[styles.col, { flexBasis: 150 }]}>
                <Text style={styles.textValue}>
                  {printHtmlField(datasheet.applicationnumber)}
                </Text>
              </View>
              <View style={[styles.col, { flexBasis: 160 }]}>
                <Text style={[styles.textKey, { paddingLeft: 10 }]}>
                  {t("number")}
                </Text>
              </View>
              <View style={[styles.col, { flexBasis: 90 }]}>
                <Text style={styles.textValue}>
                  {printHtmlField(datasheet.grant_number)}
                </Text>
              </View>
            </View>

            <View style={[styles.row, { paddingTop: 5 }]}>
              <View style={[styles.col, { flexBasis: 100 }]}>
                <Text style={styles.textKey}>{t("date")}</Text>
              </View>
              <View style={[styles.col, { flexBasis: 150 }]}>
                <Text style={[styles.textValue, { width: 100 }]}>
                  {printHtmlField(datasheet.app_date, "date")}
                </Text>
              </View>
              <View style={[styles.col, { flexBasis: 160 }]}>
                <Text style={[styles.textKey, { paddingLeft: 10 }]}>
                  {t("date")}
                </Text>
              </View>
              <View style={[styles.col, { flexBasis: 90 }]}>
                <Text style={styles.textValue}>
                  {printHtmlField(datasheet.grant_date, "date")}
                </Text>
              </View>
            </View>

            <View style={[styles.row, { paddingTop: 5 }]}>
              <View style={[styles.col, { flexBasis: 100 }]}>
                <Text style={styles.textKey}>{t("published_on")}</Text>
              </View>
              <View style={[styles.col, { flexBasis: 150 }]}>
                <Text style={[styles.textValue, { width: 100 }]}>
                  {printHtmlField(datasheet.app_pub_date, "date")}
                </Text>
              </View>
              <View style={[styles.col, { flexBasis: 160 }]}>
                <Text style={[styles.textKey, { paddingLeft: 10 }]}>
                  {t("published_on")}
                </Text>
              </View>
              <View style={[styles.col, { flexBasis: 90 }]}>
                <Text style={styles.textValue}>
                  {printHtmlField(datasheet.grant_pub_date, "date")}
                </Text>
              </View>
            </View>

            <View style={[styles.row, { paddingTop: 5 }]}>
              <View style={[styles.col, { flexBasis: 250 }]}>
                <Text>&nbsp;</Text>
              </View>

              <View style={[styles.col, { flexBasis: 160 }]}>
                <Text style={[styles.textKey, { paddingLeft: 10 }]}>
                  {t("renewal_on")}
                </Text>
              </View>
              <View style={[styles.col, { flexBasis: 90 }]}>
                <Text style={styles.textValue}>
                  {printHtmlField(datasheet.renewal_date, "date")}
                </Text>
              </View>
            </View>

            <View style={[styles.row, { paddingTop: 5 }]}>
              <View style={[styles.col, { flexBasis: 250 }]}>
                <Text>&nbsp;</Text>
              </View>

              <View style={[styles.col, { flexBasis: 160 }]}>
                <Text style={[styles.textKey, { paddingLeft: 10 }]}>
                  {t("expiration_actual")}
                </Text>
              </View>
              <View style={[styles.col, { flexBasis: 90 }]}>
                <Text style={styles.textValue}>
                  {printHtmlField(datasheet.end_date, "date")}
                </Text>
              </View>
            </View>

            <View style={[styles.row, { paddingTop: 5 }]}>
              <View style={[styles.col, { flexBasis: 250 }]}>
                <Text>&nbsp;</Text>
              </View>

              <View style={[styles.col, { flexBasis: 160 }]}>
                <Text style={[styles.textKey, { paddingLeft: 10 }]}>
                  {t("future_calc_expiration_date")}
                </Text>
              </View>
              <View style={[styles.col, { flexBasis: 90 }]}>
                <Text style={styles.textValue}>
                  {printHtmlField(datasheet.future_exp_date, "date")}
                </Text>
              </View>
            </View>

            <View style={[styles.row, { paddingTop: 20 }]}>
              <View style={[styles.col, { flexBasis: 100 }]}>
                <Text style={styles.textKey}>{t("breeders_reference")}</Text>
              </View>
              <View style={[styles.col, { flexBasis: 400 }]}>
                <Text style={styles.textValue}>
                  {printHtmlField(datasheet.breeder_ref)}
                </Text>
              </View>
            </View>

            <View style={[styles.row, { paddingTop: 5 }]}>
              <View style={[styles.col, { flexBasis: 100 }]}>
                <Text style={styles.textKey}>{t("breeder")}</Text>
              </View>
              <View style={[styles.col, { flexBasis: 400 }]}>
                <Text style={styles.textValue}>
                  {printHtmlField(datasheet.breeder)}
                </Text>
              </View>
            </View>

            <View style={[styles.row, { paddingTop: 5 }]}>
              <View style={[styles.col, { flexBasis: 100 }]}>
                <Text style={styles.textKey}>{t("applicant")}</Text>
              </View>
              <View style={[styles.col, { flexBasis: 400 }]}>
                <Text style={styles.textValue}>
                  {printHtmlField(datasheet.applicant)}
                </Text>
              </View>
            </View>

            <View style={[styles.row, { paddingTop: 5 }]}>
              <View style={[styles.col, { flexBasis: 100 }]}>
                <Text style={styles.textKey}>{t("maintainer")}</Text>
              </View>
              <View style={[styles.col, { flexBasis: 400 }]}>
                <Text style={styles.textValue}>
                  {printHtmlField(datasheet.maintainer)}
                </Text>
              </View>
            </View>

            <View style={[styles.row, { paddingTop: 5 }]}>
              <View style={[styles.col, { flexBasis: 100 }]}>
                <Text style={styles.textKey}>{t("holder")}</Text>
              </View>
              <View style={[styles.col, { flexBasis: 400 }]}>
                <Text style={styles.textValue}>
                  {printHtmlField(datasheet.titleholder)}
                </Text>
              </View>
            </View>

            <View style={[styles.row, { paddingTop: 5 }]}>
              <View style={[styles.col, { flexBasis: 100 }]}>
                <Text style={styles.textKey}>{t("other_parties")}</Text>
              </View>
              <View style={[styles.col, { flexBasis: 400 }]}>
                <Text style={styles.textValue}>
                  {printHtmlField(datasheet.other_parties)}
                </Text>
              </View>
            </View>

            <View style={[styles.row, { paddingTop: 5 }]}>
              <View style={[styles.col, { flexBasis: 100 }]}>
                <Text style={styles.textKey}>{t("remarks")}</Text>
              </View>
              <View style={[styles.col, { flexBasis: 400 }]}>
                <Text style={[styles.textValue, { minHeight: 100 }]}>
                  {printHtmlField(datasheet.remark)}
                </Text>
              </View>
            </View>

            <View break style={[styles.row, { paddingTop: 20 }]}>
              <View style={styles.fullRow}>
                <Text style={styles.regionTitle}>
                  {t("denominations_list")}
                </Text>
              </View>
            </View>

            {denominationList[i]?.denos
              ?.filter((deno: any) => deno.nature === DENOMINATION)
              .map((item: any, index: number) => {
                return (
                  <React.Fragment key={`denomination-${index}`}>
                    <View style={[styles.row, { paddingTop: 10 }]}>
                      <View style={[styles.col, { flexBasis: 100 }]}>
                        <Text style={styles.textKey}>{t("denomination")}</Text>
                      </View>
                      <View style={[styles.col, { flexBasis: 400 }]}>
                        <Text
                          style={[
                            styles.textValue,
                            {
                              fontFamily: "Open Sans",
                              fontWeight: "ultrabold",
                            }
                          ]}
                        >
                          {printHtmlField(item?.name)}
                        </Text>
                      </View>
                    </View>

                    <View style={[styles.row, { paddingTop: 5 }]}>
                      <View style={[styles.col, { flexBasis: 100 }]}>
                        <Text style={styles.textKey}>{t("type")}</Text>
                      </View>
                      <View style={[styles.col, { flexBasis: 200 }]}>
                        <Text style={[styles.textValue, { width: 170 }]}>
                          {printHtmlField(item?.nature)}
                        </Text>
                      </View>
                      <View style={[styles.col, { flexBasis: 120 }]}>
                        <Text style={[styles.textKey, { paddingLeft: 10 }]}>
                          {t("proposed_on")}
                        </Text>
                      </View>
                      <View style={[styles.col, { flexBasis: 80 }]}>
                        <Text style={styles.textValue}>
                          {printHtmlField(item?.prop_dt)}
                        </Text>
                      </View>
                    </View>
                    <View style={[styles.row, { paddingTop: 5 }]}>
                      <View style={[styles.col, { flexBasis: 100 }]}>
                        <Text style={styles.textKey}>{t("status")}</Text>
                      </View>
                      <View style={[styles.col, { flexBasis: 200 }]}>
                        <Text style={[styles.textValue, { width: 170 }]}>
                          {printHtmlField(item?.status)}
                        </Text>
                      </View>
                      <View style={[styles.col, { flexBasis: 120 }]}>
                        <Text style={[styles.textKey, { paddingLeft: 10 }]}>
                          {t("published_on")}
                        </Text>
                      </View>
                      <View style={[styles.col, { flexBasis: 80 }]}>
                        <Text style={styles.textValue}>
                          {printHtmlField(item?.pub_dt)}
                        </Text>
                      </View>
                    </View>

                    <View style={[styles.row, { paddingTop: 5 }]}>
                      <View style={[styles.col, { flexBasis: 300 }]}>
                        <Text>&nbsp;</Text>
                      </View>
                      <View style={[styles.col, { flexBasis: 120 }]}>
                        <Text style={[styles.textKey, { paddingLeft: 10 }]}>
                          {t("approved_on")}
                        </Text>
                      </View>
                      <View style={[styles.col, { flexBasis: 80 }]}>
                        <Text style={styles.textValue}>
                          {printHtmlField(item?.appr_dt)}
                        </Text>
                      </View>
                    </View>

                    <View style={[styles.row, { paddingTop: 5 }]}>
                      <View style={[styles.col, { flexBasis: 300 }]}>
                        <Text>&nbsp;</Text>
                      </View>
                      <View style={[styles.col, { flexBasis: 120 }]}>
                        <Text style={[styles.textKey, { paddingLeft: 10 }]}>
                          {t("end_date")}
                        </Text>
                      </View>
                      <View style={[styles.col, { flexBasis: 80 }]}>
                        <Text style={styles.textValue}>
                          {printHtmlField(item?.end_dt)}
                        </Text>
                      </View>
                    </View>
                  </React.Fragment>
                );
              })}

            <View style={[styles.row, { paddingTop: 20 }]}>
              <View style={styles.fullRow}>
                <Text style={styles.regionTitle}>{t("synonyms_list")}</Text>
              </View>
            </View>
            {synonymsList[i].map((synonym: string, index: number) => (
              <View key={`synonym-${index}`} style={[styles.row, { paddingTop: 10 }]}>
                <View style={[styles.col, { flexBasis: 100 }]}>
                  <Text style={[styles.textKey, { color: "#000000" }]}>
                    {t("synonym")}
                  </Text>
                </View>
                <View style={[styles.col, { flexBasis: 400 }]}>
                  <Text style={[styles.textKey, { color: "#000000" }]}>
                    {synonym}
                  </Text>
                </View>
              </View>
            ))}

            <View style={[styles.row, { paddingTop: 20 }]}>
              <View style={styles.fullRow}>
                <Text style={styles.regionTitle}>{t("tradenames")}</Text>
              </View>
            </View>

            {tradenameList[i].map((tradename: string, index: number) => (
              <View key={`tradename-${index}`} style={[styles.row, { paddingTop: 10 }]}>
                <View style={[styles.col, { flexBasis: 100 }]}>
                  <Text style={[styles.textKey, { color: "#000000" }]}>
                    {t("tradename")}
                  </Text>
                </View>
                <View style={[styles.col, { flexBasis: 400 }]}>
                  <Text style={[styles.textKey, { color: "#000000" }]}>
                    {tradename}
                  </Text>
                </View>
              </View>
            ))}

            <View style={[styles.row, { paddingTop: 20 }]}>
              <View style={styles.fullRow}>
                <Text style={styles.regionTitle}>{t("trademarks")}</Text>
              </View>
            </View>

            {trademarkList[i].map((trademark: string, index: number) => (
              <View key={`trademark-${index}`} style={[styles.row, { paddingTop: 10 }]}>
                <View style={[styles.col, { flexBasis: 100 }]}>
                  <Text style={[styles.textKey, { color: "#000000" }]}>
                    {t("trademark")}
                  </Text>
                </View>
                <View style={[styles.col, { flexBasis: 400 }]}>
                  <Text style={[styles.textKey, { color: "#000000" }]}>
                    {trademark}
                  </Text>
                </View>
              </View>
            ))}

            <Text
              style={{
                position: "absolute",
                bottom: 33,
                left: 40,
                right: 320,
                borderBottom: 1,
              }}
              fixed
            />

            <Text
              style={styles.pageNumber}
              render={({ pageNumber, totalPages }) =>
                `${pageNumber} / ${totalPages}`
              }
              fixed
            />

            <Text
              style={{
                position: "absolute",
                bottom: 33,
                left: 320,
                right: 40,
                borderBottom: 1,
              }}
              fixed
            />
          </Page>
        ))}
        {invalidDenominations}
      </>
    </Document>
  );
};

const printHtmlField = (value: any, type = "text") => {
  if (type === "date") {
    const dateFormatted = convertDate(value, GLOBAL_DATE_FORMAT, "DDMMYYYY");
    return dateFormatted ? dateFormatted : " ";
  }
  return value ? value : " ";
};
